<template>
    <b-overlay class="rounded" opacity="0.2">
        <b-card no-body>
            <div v-if="isInputTransaction && inputDetails.length > 0" class="mb-0 pt-1">
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-light">
                            <tr>
                                <th>Depo</th>
                                <th>Sipariş No</th>
                                <th>Mühür No</th>
                                <th>Miktar (lt)</th>
                                <th>Plaka</th>
                                <th>Teslim Alan</th>
                                <th>Sipariş Durumu</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="detail in inputDetails" :key="detail.Id">
                                <td class="text-nowrap">{{ getStorageName(detail) }}</td>
                                <td class="text-nowrap">{{ detail.FuelOrderNumber || "-" }}</td>
                                <td class="text-nowrap">
                                    <span v-if="transaction.Description && transaction.Description.length > 25" v-b-tooltip.hover.bottomright="transaction.Description" style="cursor: default">
                                        {{ truncateText(transaction.Description, 25) }}
                                    </span>
                                    <span v-else>{{ transaction.Description || "-" }}</span>
                                </td>
                                <td class="text-right">{{ detail.QuantityStr || 0 }}</td>
                                <td class="text-nowrap">{{ detail.VehiclePlateValue || "-" }}</td>
                                <td class="text-nowrap">{{ getEmployeeName(detail.ReceivingEmployeeId) }}</td>
                                <td class="text-nowrap">
                                    <span v-if="getOrderStatusInfo(detail)" :class="['badge', 'badge-pill', `badge-${getOrderStatusInfo(detail).variant}`]">
                                        {{ getOrderStatusInfo(detail).name }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="isBalanceTransaction && balanceDetails.length > 0" class="mb-0 pt-1">
                <div v-if="transaction.Description" class="px-1 mb-50 border-bottom pb-50">
                    <strong class="text-muted">Açıklama:</strong>
                    <span class="ml-1"> {{ transaction.Description }} </span>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-light">
                            <tr>
                                <th>Depo</th>
                                <th class="text-right">Sistemdeki Stok (Önceki)</th>
                                <th class="text-right">Fiziki/Sayım (Sonraki)</th>
                                <th class="text-right">Fark / Değişim</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="detail in balanceDetails" :key="detail.Id">
                                <td class="text-nowrap">{{ detail.FuelStorageName + " (" + detail.FuelStorageCode + ")" }}</td>
                                <td class="text-right">{{ formatQuantity(detail.InitialBalance) }}</td>
                                <td class="text-right">{{ formatQuantity(detail.FinalBalance) }}</td>
                                <td class="text-right font-weight-bold" :class="detail.FinalBalance - detail.InitialBalance >= 0 ? 'text-success' : 'text-danger'">{{ detail.FinalBalance - detail.InitialBalance > 0 ? "+" : "" }}{{ formatQuantity(detail.FinalBalance - detail.InitialBalance) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="!isInputTransaction && outputDetails.length > 0" class="mb-0 pt-50">
                <div v-if="transaction.Description" class="px-1 my-50 border-bottom pb-50">
                    <strong class="text-muted">Açıklama:</strong>
                    <span v-if="transaction.Description.length > 95" :id="`transaction-desc-popover-${transaction.Id}`" class="c-pointer text-primary ml-1">
                        {{ truncateText(transaction.Description, 95) }}
                    </span>
                    <span v-else class="ml-1"> {{ transaction.Description }} </span>
                    <b-popover v-if="transaction.Description.length > 95" :target="`transaction-desc-popover-${transaction.Id}`" triggers="click blur" placement="top">
                        {{ transaction.Description }}
                    </b-popover>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-light">
                            <tr>
                                <th class="text-nowrap">Depo</th>
                                <th class="text-nowrap">Araç Plakası</th>
                                <th class="text-nowrap">Treyler Plakası</th>
                                <th class="text-nowrap">Sürücü</th>
                                <th class="text-nowrap">Araç KM</th>
                                <th class="text-nowrap">Araç Klima Sayaç</th>
                                <th class="text-nowrap">Yakıt Tipi</th>
                                <th class="text-nowrap">Pompa 1 (lt)</th>
                                <th class="text-nowrap">Pompa 1 Sayaç</th>
                                <th class="text-nowrap">Pompa 2 (lt)</th>
                                <th class="text-nowrap">Pompa 2 Sayaç</th>
                                <th class="text-nowrap">Klima 1 (lt)</th>
                                <th class="text-nowrap">Klima 1 Sayaç</th>
                                <th class="text-nowrap">Klima 2 (lt)</th>
                                <th class="text-nowrap">Klima 2 Sayaç</th>
                                <th class="text-nowrap">Toplam (lt)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="detail in outputDetails" :key="detail.Id">
                                <td class="text-nowrap">{{ getStorageName(detail) }}</td>
                                <td class="text-nowrap">{{ detail.VehiclePlateValue || "-" }}</td>
                                <td class="text-nowrap">{{ detail.TrailerPlateValue || "-" }}</td>
                                <td class="text-nowrap">{{ getEmployeeName(detail.DriverEmployeeId) }}</td>
                                <td class="text-right text-nowrap">{{ detail.VehicleCurrentKMStr || 0 }}</td>
                                <td class="text-right text-nowrap">{{ detail.VehicleAcCurrentCounterStr || 0 }}</td>
                                <td class="text-nowrap">{{ getFuelTypeName(detail) }}</td>
                                <td class="text-right text-nowrap">{{ detail.FuelPump1LTStr || 0 }}</td>
                                <td class="text-right text-nowrap">{{ detail.FuelPump1CounterStr || 0 }}</td>
                                <td class="text-right text-nowrap">{{ detail.FuelPump2LTStr || 0 }}</td>
                                <td class="text-right text-nowrap">{{ detail.FuelPump2CounterStr || 0 }}</td>
                                <td class="text-right">{{ detail.Ac1FuelPumpLTStr || 0 }}</td>
                                <td class="text-right">{{ detail.Ac1FuelCurrentCounterStr || 0 }}</td>
                                <td class="text-right">{{ detail.Ac2FuelPumpLTStr || 0 }}</td>
                                <td class="text-right">{{ detail.Ac2FuelCurrentCounterStr || 0 }}</td>
                                <td class="text-right font-weight-bold text-nowrap">{{ detail.QuantityStr || 0 }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Transfer İşlemi -->
            <div v-if="isTransferTransaction && transferDetails.length > 0" class="transfer-container">
                <div v-if="transaction.Description" class="transfer-desc">
                    <span class="text-muted font-weight-bold">Açıklama:</span>
                    <span class="ml-1">{{ transaction.Description }}</span>
                </div>
                <div class="transfer-tables">
                    <div class="transfer-card transfer-card--source">
                        <div class="transfer-card__header">
                            <feather-icon icon="ArrowUpCircleIcon" size="14" class="mr-1" />
                            Kaynak (Çıkış)
                        </div>
                        <table class="table table-sm table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>Depo</th>
                                    <th>Yetkili</th>
                                    <th>Yakıt Tipi</th>
                                    <th>Kantar (Ton)</th>
                                    <th>Kantar Fişi</th>
                                    <th class="text-right">Miktar (lt)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in sourceStorages" :key="'src-' + idx">
                                    <td>{{ item.name }}</td>
                                    <td>{{ getEmployeeName(item.supervisorId) }}</td>
                                    <td>{{ transaction.FuelTypeName || "-" }}</td>
                                    <td>{{ item.tonnage ? formatQuantity(item.tonnage) : "-" }}</td>
                                    <td>
                                        <b-button v-if="item.file" variant="flat-primary" size="sm" class="py-0 px-50 cursor-pointer text-primary" @click="viewFile(item.file)">
                                            <feather-icon icon="ExternalLinkIcon" size="11" class="mr-25" />
                                            <span class="font-weight-bold font-small-2">Görüntüle</span>
                                        </b-button>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="text-right font-weight-bold">{{ formatQuantity(item.quantity) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="transfer-card__footer transfer-card__footer--source">
                            <span>TOPLAM ÇIKIŞ</span>
                            <span>{{ formatQuantity(sourceTotalQuantity) }}</span>
                        </div>
                    </div>

                    <div class="transfer-card transfer-card--dest">
                        <div class="transfer-card__header transfer-card__header--dest">
                            <feather-icon icon="ArrowDownCircleIcon" size="14" class="mr-1" />
                            Hedef (Giriş)
                        </div>
                        <table class="table table-sm table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>Depo</th>
                                    <th>Yetkili</th>
                                    <th>Durum</th>
                                    <th>Yakıt Tipi</th>
                                    <th>Kantar (Ton)</th>
                                    <th>Kantar Fişi</th>
                                    <th class="text-right">Miktar (lt)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in destStorages" :key="'dest-' + idx">
                                    <td>{{ item.name }}</td>
                                    <td>{{ getEmployeeName(item.supervisorId) }}</td>
                                    <td>
                                        <div v-if="item.isConfirmed" class="d-flex align-items-center text-success">
                                            <feather-icon icon="CheckCircleIcon" size="14" class="mr-50" />
                                            <span class="font-small-2 font-weight-bold">Onaylandı</span>
                                        </div>
                                        <div v-else class="d-flex align-items-center text-warning">
                                            <feather-icon icon="ClockIcon" size="14" class="mr-50" />
                                            <span class="font-small-2 font-weight-bold">Onay Bekliyor</span>
                                        </div>
                                    </td>
                                    <td>{{ transaction.FuelTypeName || "-" }}</td>
                                    <td>{{ item.tonnage ? formatQuantity(item.tonnage) : "-" }}</td>
                                    <td>
                                        <b-button v-if="item.file" variant="flat-primary" size="sm" class="py-0 px-50 text-primary cursor-pointer" @click="viewFile(item.file)">
                                            <feather-icon icon="ExternalLinkIcon" size="11" class="mr-25" />
                                            <span class="font-weight-bold font-small-2">Görüntüle</span>
                                        </b-button>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="text-right font-weight-bold">{{ formatQuantity(item.quantity) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="transfer-card__footer transfer-card__footer--dest">
                            <span>TOPLAM GİRİŞ</span>
                            <span>{{ formatQuantity(destTotalQuantity) }}</span>
                        </div>
                    </div>
                </div>

                <div class="transfer-carrier">
                    <div class="transfer-carrier__header">
                        <feather-icon icon="TruckIcon" size="14" class="mr-1" />
                        Taşıma Bilgileri
                    </div>
                    <div class="transfer-carrier__body">
                        <div class="transfer-carrier__item">
                            <span class="transfer-carrier__label">Araç Plaka</span>
                            <span class="transfer-carrier__value">{{ getPlateValue(transferCarrierInfo.vehiclePlateId) }}</span>
                        </div>
                        <div class="transfer-carrier__item">
                            <span class="transfer-carrier__label">Treyler</span>
                            <span class="transfer-carrier__value">{{ getPlateValue(transferCarrierInfo.trailerPlateId) || "-" }}</span>
                        </div>
                        <div class="transfer-carrier__item">
                            <span class="transfer-carrier__label">Sürücü</span>
                            <span class="transfer-carrier__value">{{ getEmployeeName(transferCarrierInfo.carrierId) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Araçtan Depoya Aktarım (type 6) -->
            <div v-if="isFromVehicleToStorageTransaction && fromVehicleToStorageDetails.length > 0" class="mb-0 pt-1">
                <div v-if="transaction.Description" class="px-1 mb-50 border-bottom pb-50">
                    <strong class="text-muted">Açıklama:</strong>
                    <span class="ml-1">{{ transaction.Description }}</span>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                        <thead class="table-light">
                            <tr>
                                <th class="text-nowrap">Kaynak Araç</th>
                                <th class="text-nowrap">Hedef Depo</th>
                                <th class="text-nowrap">Yakıt Tipi</th>
                                <th class="text-nowrap text-right">Miktar (lt)</th>
                                <th class="text-nowrap">Sorumlu</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="detail in fromVehicleToStorageDetails" :key="detail.Id">
                                <td class="text-nowrap">{{ detail.VehiclePlateValue || getPlateFromVehiclePlates(detail.VehiclePlateId) || '-' }}</td>
                                <td class="text-nowrap">{{ getStorageName(detail) }}</td>
                                <td class="text-nowrap">{{ transaction.FuelTypeName || '-' }}</td>
                                <td class="text-right font-weight-bold">{{ detail.QuantityStr || formatQuantity(detail.Quantity) }}</td>
                                <td class="text-nowrap">{{ getEmployeeName(detail.DriverEmployeeId) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="(isInputTransaction && inputDetails.length === 0) || (isOutputTransaction && outputDetails.length === 0) || (isTransferTransaction && transferDetails.length === 0) || (isFromVehicleToStorageTransaction && fromVehicleToStorageDetails.length === 0)" class="text-muted text-center py-3">
                <feather-icon icon="InfoIcon" size="16" class="me-1"></feather-icon>
                Bu işlem için detay bilgisi bulunmamaktadır.
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import { BCard, BPopover, BOverlay, VBTooltip, BButton } from "bootstrap-vue";

export default {
    name: "FuelStorageTransactionDetail",
    components: {
        BCard,
        BPopover,
        BOverlay,
        BButton,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        transaction: {
            type: Object,
            required: true,
        },
        allEmployees: {
            type: Array,
            default: () => [],
        },
        fuelTypes: {
            type: Array,
            default: () => [],
        },
        vehiclePlates: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        isInputTransaction() {
            return this.transaction.FuelTransactionType === 1;
        },
        inputDetails() {
            return this.transaction.FuelStorageInputTransactionDetails || [];
        },
        isBalanceTransaction() {
            return this.transaction.FuelTransactionType === 5;
        },
        balanceDetails() {
            return this.transaction.Balances || this.transaction.FuelStorageBalanceTransactionDetails || [];
        },
        outputDetails() {
            return this.transaction.FuelStorageOutputTransactionDetails || [];
        },
        isTransferTransaction() {
            return this.transaction.FuelTransactionType === 3;
        },
        transferDetails() {
            return this.transaction.FuelStorageTransferTransactionDetails || [];
        },
        sourceStorages() {
            if (!this.transferDetails || this.transferDetails.length === 0) return [];
            return this.transferDetails
                .filter((d) => d.TransactionType === 0)
                .map((d) => ({
                    name: d.FuelStorageName ? `${d.FuelStorageName} (${d.FuelStorageCode})` : "-",
                    supervisorId: d.SupervisorEmployeeId,
                    quantity: d.Quantity,
                    isConfirmed: d.IsConfirmed,
                    tonnage: d.Tonnage,
                    file: d.File,
                }));
        },
        destStorages() {
            if (!this.transferDetails || this.transferDetails.length === 0) return [];
            return this.transferDetails
                .filter((d) => d.TransactionType === 1)
                .map((d) => ({
                    name: d.FuelStorageName ? `${d.FuelStorageName} (${d.FuelStorageCode})` : "-",
                    supervisorId: d.SupervisorEmployeeId,
                    quantity: d.Quantity,
                    isConfirmed: d.IsConfirmed,
                    tonnage: d.Tonnage,
                    file: d.File,
                }));
        },
        sourceTotalQuantity() {
            return this.sourceStorages.reduce((sum, s) => sum + (s.quantity || 0), 0);
        },
        destTotalQuantity() {
            return this.destStorages.reduce((sum, s) => sum + (s.quantity || 0), 0);
        },
        transferCarrierInfo() {
            const firstDetail = this.transferDetails[0];
            if (!firstDetail) return {};
            return {
                vehiclePlateId: firstDetail.VehiclePlateId,
                trailerPlateId: firstDetail.TrailerPlateId,
                carrierId: firstDetail.CarrierEmployeeId,
            };
        },
        isFromVehicleToStorageTransaction() {
            return this.transaction.FuelTransactionType === 6;
        },
        fromVehicleToStorageDetails() {
            return this.transaction.FuelStorageInputTransactionDetails || [];
        },
        isOutputTransaction() {
            return this.transaction.FuelTransactionType === 0;
        },
    },
    methods: {
        getEmployeeName(employeeId) {
            if (!employeeId || this.allEmployees.length === 0) {
                return "-";
            }
            const employee = this.allEmployees.find((emp) => emp.Id && emp.Id.toLowerCase() === employeeId.toLowerCase());

            return employee ? employee.FullName : "-";
        },
        viewFile(url) {
            if (url) {
                window.open(url, "_blank");
            }
        },
        getStorageName(detail) {
            if (detail.FuelStorageVisibleName) {
                return detail.FuelStorageVisibleName;
            }

            if (detail.FuelStorage && detail.FuelStorage.Name && detail.FuelStorage.Code) {
                return `${detail.FuelStorage.Name} - ${detail.FuelStorage.Code}`;
            }

            if (detail.FuelStorage && detail.FuelStorage.Name) {
                return detail.FuelStorage.Name;
            }

            return "-";
        },
        truncateText(text, length) {
            if (text && text.length > length) {
                return text.substring(0, length) + "...";
            }
            return text;
        },
        getFuelTypeName(detail) {
            if (!detail || !detail.FuelStorage || !detail.FuelStorage.FuelStorageFuelTypeId) {
                return "-";
            }
            const fuelTypeId = detail.FuelStorage.FuelStorageFuelTypeId;
            const fuelType = this.fuelTypes.find((ft) => ft.value === fuelTypeId);
            return fuelType ? fuelType.text : "-";
        },
        getOrderStatusInfo(detail) {
            if (!detail || detail.FuelOrder === null || detail.FuelOrder.OrderStatus === undefined) {
                return null;
            }
            const statusId = detail.FuelOrder.OrderStatus;

            const idToNameMap = {
                0: "Planlandı",
                1: "Sipariş Verildi",
                2: "Bakiyeli Kapatıldı",
                3: "Kapatıldı",
                4: "İptal Edildi",
            };

            const idToVariantMap = {
                0: "info",
                1: "warning",
                2: "secondary",
                3: "success",
                4: "danger",
            };

            return {
                name: idToNameMap[statusId] || "Bilinmiyor",
                variant: idToVariantMap[statusId] || "light",
            };
        },
        formatQuantity(value) {
            if (!value) return "0";
            return new Intl.NumberFormat("tr-TR").format(value);
        },
        getPlateFromVehiclePlates(plateId) {
            if (!plateId) return null;
            const plate = this.vehiclePlates.find((p) => p.Id === plateId);
            return plate ? plate.PlateValue : null;
        },
        getPlateValue(plateId) {
            if (!plateId) return "-";
            const plate = this.vehiclePlates.find((p) => p.Id === plateId);
            return plate ? plate.PlateValue : "-";
        },
    },
};
</script>
<style scoped>
.transfer-container {
    padding: 0.5rem;
}

.transfer-desc {
    font-size: 0.8rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 4px;
    margin-bottom: 0.5rem;
}

.transfer-tables {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

.transfer-card {
    flex: 1;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.transfer-card__header {
    display: flex;
    align-items: center;
    padding: 0.3rem 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    background: rgba(234, 84, 85, 0.12);
    color: #dc3545;
}

.transfer-card__header--dest {
    background: rgba(40, 199, 111, 0.12);
    color: #28a745;
}

.transfer-card table {
    font-size: 0.8rem;
}

.transfer-card th {
    padding: 0.25rem 0.4rem !important;
    background: #f8f9fa;
    font-weight: 600;
    white-space: nowrap;
}

.transfer-card td {
    padding: 0.25rem 0.4rem !important;
    vertical-align: top;
}

.transfer-card__footer {
    display: flex;
    justify-content: space-between;
    padding: 0.3rem 0.5rem;
    font-size: 0.85rem;
    font-weight: 700;
    margin-top: auto;
}

.transfer-card__footer--source {
    background: rgba(234, 84, 85, 0.12);
    color: #dc3545;
}

.transfer-card__footer--dest {
    background: rgba(40, 199, 111, 0.12);
    color: #28a745;
}

.transfer-carrier {
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e9ecef;
    overflow: hidden;
}

.transfer-carrier__header {
    display: flex;
    align-items: center;
    padding: 0.3rem 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    background: #e9ecef;
    color: #6c757d;
}

.transfer-carrier__body {
    display: flex;
    gap: 1.5rem;
    padding: 0.4rem 0.5rem;
    font-size: 0.8rem;
}

.transfer-carrier__label {
    display: block;
    font-size: 0.75rem;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.transfer-carrier__value {
    font-weight: 600;
    color: #212529;
}

@media (max-width: 767px) {
    .transfer-tables {
        flex-direction: column;
    }
}
</style>
